import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import HomeTemplate from '../templates/HomeTemplate'
import { Calendar, Clock, User, Tag, ArrowLeft, Share2, Heart, MessageCircle } from 'lucide-react'
import { BLOG_POSTS } from '../data/blogPosts.jsx'
import useCanonicalUrl from '../hooks/useCanonicalUrl'
import './BlogPostPage.css'

const BlogPostPage = () => {
  useCanonicalUrl();
  const { slug } = useParams()
  const navigate = useNavigate()
  const [post, setPost] = useState(null)
  const [relatedPosts, setRelatedPosts] = useState([])
  const [tableOfContents, setTableOfContents] = useState([])
  const [activeHeading, setActiveHeading] = useState('')
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, height: 0, opacity: 0 })
  const tocListRef = useRef(null)
  const [isLiked, setIsLiked] = useState(false)

  useEffect(() => {
    // Find the blog post
    const foundPost = BLOG_POSTS.find(p => p.slug === slug)

    if (!foundPost) {
      navigate('/blog/')
      return
    }

    setPost(foundPost)

    // Set meta tags and structured data
    document.title = `${foundPost.title} - EveryKits Blog`

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', foundPost.description)
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]')
    const ogDescription = document.querySelector('meta[property="og:description"]')
    const ogImage = document.querySelector('meta[property="og:image"]')
    const ogUrl = document.querySelector('meta[property="og:url"]')
    const metaTitleTag = document.querySelector('meta[name="title"]')
    const twitterTitle = document.querySelector('meta[property="twitter:title"]')
    const twitterDescription = document.querySelector('meta[property="twitter:description"]')
    const twitterImage = document.querySelector('meta[property="twitter:image"]')
    const twitterUrl = document.querySelector('meta[property="twitter:url"]')
    const absoluteImage = `https://everykits.com${foundPost.featuredImage}`
    const postUrl = `https://everykits.com/blog/${foundPost.slug}/`

    if (ogTitle) ogTitle.setAttribute('content', foundPost.title)
    if (ogDescription) ogDescription.setAttribute('content', foundPost.description)
    if (ogImage) ogImage.setAttribute('content', absoluteImage)
    if (ogUrl) ogUrl.setAttribute('content', postUrl)
    if (metaTitleTag) metaTitleTag.setAttribute('content', `${foundPost.title} - EveryKits Blog`)
    if (twitterTitle) twitterTitle.setAttribute('content', foundPost.title)
    if (twitterDescription) twitterDescription.setAttribute('content', foundPost.description)
    if (twitterImage) twitterImage.setAttribute('content', absoluteImage)
    if (twitterUrl) twitterUrl.setAttribute('content', postUrl)

    // Add structured data for blog post
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": foundPost.title,
      "description": foundPost.description,
      "image": absoluteImage,
      "datePublished": foundPost.publishDate,
      "dateModified": foundPost.publishDate,
      "author": {
        "@type": "Organization",
        "name": foundPost.author
      },
      "publisher": {
        "@type": "Organization",
        "name": "EveryKits",
        "logo": {
          "@type": "ImageObject",
          "url": "https://everykits.com/logo.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://everykits.com/blog/${foundPost.slug}/`
      },
      "keywords": foundPost.tags.join(', '),
      "wordCount": foundPost.content.split(' ').length,
      "articleSection": foundPost.category
    }

    // Add structured data to head
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(structuredData)
    document.head.appendChild(script)

    // Generate table of contents
    const toc = generateTableOfContents(foundPost.content)
    setTableOfContents(toc)

    // Find related posts. A strict same-category filter left posts that are
    // the only entry in their category (e.g. Security, Wellness, Education)
    // with zero related articles, which is bad for both readers and internal
    // linking. Score by category + shared tags instead, with recency as a
    // tiebreaker, so every post always surfaces its closest neighbors.
    const related = BLOG_POSTS
      .filter(p => p.id !== foundPost.id)
      .map(p => {
        let score = 0
        if (p.category === foundPost.category) score += 3
        const sharedTags = (p.tags || []).filter(t => (foundPost.tags || []).includes(t)).length
        score += sharedTags
        return { post: p, score }
      })
      .sort((a, b) => b.score - a.score || new Date(b.post.publishDate) - new Date(a.post.publishDate))
      .slice(0, 3)
      .map(entry => entry.post)
    setRelatedPosts(related)

    return () => {
      document.head.removeChild(script)
    }
  }, [slug, navigate])

  const generateTableOfContents = (content) => {
    const headings = content.match(/^#{1,3}\s+(.+)$/gm) || []
    return headings.map((heading, index) => {
      const level = heading.match(/^#/)[0].length
      const title = heading.replace(/^#+\s+/, '')
      const id = `heading-${index}`
      return { level, title, id }
    })
  }

  // Scroll-spy: highlight the TOC entry for whichever heading is currently
  // in view, so the reader always sees where they are in the article.
  useEffect(() => {
    if (tableOfContents.length === 0) return

    const headingElements = tableOfContents
      .map(item => document.getElementById(item.id))
      .filter(Boolean)

    if (headingElements.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveHeading(entry.target.id)
          }
        })
      },
      { rootMargin: '-100px 0px -70% 0px', threshold: 0 }
    )

    headingElements.forEach(el => observer.observe(el))

    return () => observer.disconnect()
  }, [tableOfContents, post])

  // Slide the TOC indicator bar to sit exactly next to whichever link is
  // active, so the "you are here" marker moves smoothly as the reader
  // scrolls or clicks — matching how docs sites like Stripe/Notion do it.
  useEffect(() => {
    if (!activeHeading || !tocListRef.current) {
      setIndicatorStyle(prev => ({ ...prev, opacity: 0 }))
      return
    }
    const activeLink = tocListRef.current.querySelector(`a[href$="#${activeHeading}"]`)
    if (activeLink) {
      setIndicatorStyle({
        top: activeLink.offsetTop,
        height: activeLink.offsetHeight,
        opacity: 1
      })
    }
  }, [activeHeading, tableOfContents])

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }
    return new Date(dateString).toLocaleDateString('en-US', options)
  }

  const renderContent = (content) => {
    // Convert markdown-style headers to HTML
    let processedContent = content
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code>$1</code>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/^\d+\.\s+(.+)$/gm, '<li>$1</li>')
      .replace(/^-\s+(.+)$/gm, '<li>$1</li>')

    // Wrap in paragraphs
    if (!processedContent.startsWith('<h') && !processedContent.startsWith('<p>')) {
      processedContent = `<p>${processedContent}</p>`
    }

    // Convert lists
    processedContent = processedContent.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')

    // Assign anchor IDs to headings directly on the HTML string (order-matched
    // to generateTableOfContents) instead of splitting by line/tag, which used
    // to drop any content that ended up sharing a "line" with a heading after
    // the paragraph-break replacement above.
    let idIndex = 0
    processedContent = processedContent.replace(/<h([1-3])>(.*?)<\/h[1-3]>/g, (match, level, text) => {
      const id = `heading-${idIndex}`
      idIndex++
      return `<h${level} id="${id}">${text}</h${level}>`
    })

    return { __html: processedContent }
  }

  const handleShare = (platform) => {
    const url = window.location.href
    const text = post.title

    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      reddit: `https://reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}`
    }

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400')
    }
  }

  const handleLike = () => {
    setIsLiked(!isLiked)
    // In a real app, this would make an API call
  }

  const scrollToHeading = (id) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  if (!post) {
    return <div>Loading...</div>
  }

  return (
    <HomeTemplate>
      <div className="blog-post">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/">Home</Link>
          <span className="separator">/</span>
          <Link to="/blog/">Blog</Link>
          <span className="separator">/</span>
          <span className="current">{post.title}</span>
        </nav>

        {/* Back Button */}
        <button className="back-button" onClick={() => navigate('/blog/')}>
          <ArrowLeft className="back-icon" />
          Back to Blog
        </button>

        {/* Article Header */}
        <article className="blog-article">
          <header className="article-header">
            <div className="article-category">
              <Tag className="category-icon" />
              {post.category}
            </div>

            <h1 className="article-title">{post.title}</h1>

            <p className="article-description">{post.description}</p>

            <div className="article-meta">
              <div className="meta-item">
                <User className="meta-icon" />
                <span>{post.author}</span>
              </div>
              <div className="meta-item">
                <Calendar className="meta-icon" />
                <span>{formatDate(post.publishDate)}</span>
              </div>
              <div className="meta-item">
                <Clock className="meta-icon" />
                <span>{post.readTime}</span>
              </div>
            </div>

            <div className="article-tags">
              {post.tags.map((tag, index) => (
                <span key={index} className="tag">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="featured-image">
              <img
                src={post.featuredImage}
                alt={post.title}
                onError={(e) => {
                  e.target.src = '/logo/default-blog-image.jpg'
                }}
              />
            </div>
          </header>

          <div className="article-content-wrapper">
            {/* Table of Contents */}
            {tableOfContents.length > 0 && (
              <aside className="table-of-contents">
                <h3>Table of Contents</h3>
                <ul ref={tocListRef}>
                  <li
                    className="toc-indicator"
                    style={{
                      transform: `translateY(${indicatorStyle.top}px)`,
                      height: `${indicatorStyle.height}px`,
                      opacity: indicatorStyle.opacity
                    }}
                    aria-hidden="true"
                  />
                  {tableOfContents.map((item, index) => (
                    <li key={index} className={`level-${item.level}`}>
                      <Link
                        to={`#${item.id}`}
                        className={activeHeading === item.id ? 'active' : ''}
                        onClick={(e) => {
                          e.preventDefault()
                          setActiveHeading(item.id)
                          scrollToHeading(item.id)
                        }}>
                        {item.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </aside>
            )}

            {/* Article Content */}
            <div
              className="article-content"
              dangerouslySetInnerHTML={renderContent(post.content)}
            />
          </div>

          {/* Article Footer */}
          <footer className="article-footer">
            <div className="article-actions">
              <div className="action-buttons">
                <button
                  className={`action-btn ${isLiked ? 'liked' : ''}`}
                  onClick={handleLike}
                >
                  <Heart className={`action-icon ${isLiked ? 'filled' : ''}`} />
                  <span>{isLiked ? 'Liked' : 'Like'}</span>
                </button>

                <div className="share-buttons">

                  <button className="action-btn" onClick={() => handleShare('facebook')}>
                    Share on Facebook
                  </button>
                  <button className="action-btn" onClick={() => handleShare('linkedin')}>
                    Share on Instagram
                  </button>
                </div>
              </div>
            </div>

            <div className="author-bio">
              <span className="author-bio-label">Written by</span>
              <div className="author-info">
                <div className="author-avatar">
                  <img
                    src="/logo/author-avatar.jpg"
                    alt={post.author}
                    onError={(e) => {
                      e.target.src = '/logo/default-blog-image.jpg'
                    }}
                  />
                </div>
                <div className="author-details">
                  <h3>{post.author}</h3>
                  <span className="author-role">Product &amp; Content Team</span>
                  <p>The EveryKits team builds and writes about free, no-login browser tools — this guide reflects hands-on testing with the utilities and workflows described above, kept up to date as tools and best practices change.</p>
                </div>
              </div>
            </div>
          </footer>
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="related-posts">
            <h2>Related Articles</h2>
            <div className="related-posts-grid">
              {relatedPosts.map((relatedPost) => (
                <article key={relatedPost.id} className="related-post-card">
                  <div className="related-post-image">
                    <img
                      src={relatedPost.featuredImage}
                      alt={relatedPost.title}
                      onError={(e) => {
                        e.target.src = '/logo/default-blog-image.jpg'
                      }}
                    />
                  </div>
                  <div className="related-post-content">
                    <h3>
                      <Link to={`/blog/${relatedPost.slug}/`}>
                        {relatedPost.title}
                      </Link>
                    </h3>
                    <p>{relatedPost.description}</p>
                    <div className="related-post-meta">
                      <Calendar className="meta-icon" />
                      <span suppressHydrationWarning>{formatDate(post.publishDate)}</span>
                      <span className="meta-item">
                        <Clock className="meta-icon" />
                        {relatedPost.readTime}
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Comments Section */}
        <section className="comments-section">
          <h2>Comments</h2>
          <div className="comments-placeholder">
            <MessageCircle className="comments-icon" />
            <p>Comments coming soon! In the meantime, feel free to share your thoughts on social media.</p>
          </div>
        </section>
      </div>
    </HomeTemplate>
  )
}

export default BlogPostPage